
import * as React from 'react';
import { useState, useEffect } from 'react';
import { 
  ColumnDef, 
  flexRender, 
  getCoreRowModel, 
  useReactTable, 
  SortingState, 
  getSortedRowModel, 
  ColumnFiltersState, 
  getFilteredRowModel, 
  getPaginationRowModel,
  RowSelectionState
} from "@tanstack/react-table";
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { getUserInfo, saveUserInfo } from '@/services/userService';
import { DataTableCardView } from '@/components/ui/DataTableCardView';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { DataTablePagination } from '@/components/ui/datatablepagination';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { useTenancy } from '@/contexts/TenancyContext';
import { TOUR_STEP_IDS } from '@/lib/tour-constants';

export interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  pageSize: number;
  isLoading?: boolean;
  onActions?: {
    onRead?: (row: TData) => void;
    onViewHistory?: (row: TData) => void;
    onReview?: (row: TData) => void;
    onEdit?: (row: TData) => void;
    onDelete?: (row: TData) => void;
    onDownload?: (row: TData) => void;
    userIdToName: Record<string, string> | ((userId: string) => string);
    onPrivacyView?: (row: TData) => void;
    onPrivacyHistory?: (row: TData) => void;
    onPrivacyEdit?: (row: TData) => void;
    onCollectionView?: (row: TData) => void;
    onCollectionHistory?: (row: TData) => void;
    onCollectionEdit?: (row: TData) => void;
  };
  onRowClick?: (row: TData) => void;
  /** Initial category filter: 'all', 'policy', or 'procedure' */
  initialCategoryFilter?: 'all' | 'policy' | 'procedure';
}


export function DataTable<TData, TValue>({
  columns,
  data,
  pageSize = 5,
  onActions,
  onRowClick,
  initialCategoryFilter = 'all',
  isLoading: externalLoading,
}: DataTableProps<TData, TValue>): React.ReactElement {
  const { activeTenancy } = useTenancy();
  const isMobile = useMediaQuery('(max-width: 767px)');
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [policyTypeFilter, setPolicyTypeFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState<'all' | 'policy' | 'procedure'>(initialCategoryFilter);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [internalLoading, setInternalLoading] = useState(true);
  const [userPageSize, setUserPageSize] = useState(pageSize);
  
  // Use external loading if provided, otherwise use internal loading
  const isLoading = externalLoading !== undefined ? externalLoading : internalLoading;

  // Filter data based on category - must be before useReactTable
  const filteredData = React.useMemo(() => {
    if (categoryFilter === 'all') return data;
    return data.filter((item: any) => {
      const docCategory = item.policy_metadata?.documentCategory;
      
      if (categoryFilter === 'procedure') {
        return docCategory === 'procedure';
      }
      // For 'policy', include everything that's not a procedure
      return docCategory !== 'procedure';
    });
  }, [data, categoryFilter]);

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      rowSelection,
    },
    initialState: {
      pagination: {
        pageSize: userPageSize || pageSize,
        pageIndex: 0,
      },
      columnVisibility: {
        'type': false
      }
    }
  });

  // Load user's saved rows per page preference
  useEffect(() => {
    if (!activeTenancy?.id || ["Loading...", ""].includes(activeTenancy.id)) return;
    const loadUserPreference = async () => {
      try {
        const userInfo = await getUserInfo() as { rowsPerPage?: number };
        if (userInfo?.rowsPerPage) {
          setUserPageSize(userInfo.rowsPerPage);
          table.setPageSize(userInfo.rowsPerPage);
        }
      } catch (error) {
        console.error('Error loading user preferences:', error);
      } finally {
        setInternalLoading(false);
      }
    };

    loadUserPreference();
  }, [activeTenancy.id]);

  // Update table's page size when userPageSize changes
  useEffect(() => {
    if (userPageSize) {
      table.setPageSize(userPageSize);
    }
  }, [userPageSize, table]);

  // Save rows per page preference when it changes
  const handlePageSizeChange = async (newPageSize: number) => {
    setUserPageSize(newPageSize);
    // Reset to first page when page size changes
    table.setPageIndex(0);
    
    try {
      await saveUserInfo({ rowsPerPage: newPageSize });
      // toast.success('Rows per page preference saved successfully');
    } catch (error) {
      console.error('Error saving rows per page preference:', error);
      // toast.error('Failed to save rows per page preference. Please try again.');
    }
  };

  // Handle row click for mobile card view
  const handleRowClick = (row: any) => {
    if (onRowClick) {
      onRowClick(row.original);
    }
  };

  // Update category filter when initialCategoryFilter changes (e.g., from URL)
  useEffect(() => {
    setCategoryFilter(initialCategoryFilter);
  }, [initialCategoryFilter]);

  // Reset to first page when filter changes
  useEffect(() => {
    table.setPageIndex(0);
  }, [categoryFilter]);

  return (
    <div className="space-y-4">
      {/* Search and Filter Controls */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="flex-1 min-w-[200px]">
          <Input
            id={TOUR_STEP_IDS.SEARCH_BAR}
            placeholder={categoryFilter === 'procedure' ? "Search Processes" : "Search Policies"}
            value={(table.getColumn('policy_name')?.getFilterValue() as string) ?? ''}
            onChange={(event) =>
              table.getColumn('policy_name')?.setFilterValue(event.target.value)
            }
          />
        </div>
        {/* Filter Controls Container */}
        <div id={TOUR_STEP_IDS.FILTER_BAR} className="flex flex-wrap gap-2">
          {/* Category Filter (Policy/Process) */}
          <div className="w-full sm:w-auto">
          <Select
            value={categoryFilter}
            onValueChange={(value: 'all' | 'policy' | 'procedure') => {
              setCategoryFilter(value);
            }}
          >
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="All Items" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Items</SelectItem>
              <SelectItem value="policy">Policies Only</SelectItem>
              <SelectItem value="procedure">Processes Only</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {/* Policy Type Filter (default/custom) - only show when viewing policies */}
        {categoryFilter !== 'procedure' && (
          <div className="w-full sm:w-auto">
            <Select
              value={policyTypeFilter}
              onValueChange={(value) => {
                setPolicyTypeFilter(value);
                table.getColumn('type')?.setFilterValue(
                  value === 'all' ? undefined : value
                );
              }}
            >
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="All Policy Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Policy Types</SelectItem>
                <SelectItem value="default">de.iterate Policy</SelectItem>
                <SelectItem value="upload">Custom Policy</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      </div>

      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-10 flex items-center justify-center">
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm text-muted-foreground">Loading policies...</span>
          </div>
        </div>
      )}

      {/* Mobile Card View */}
      {isMobile ? (
        <DataTableCardView 
          data={table.getRowModel().rows.map(row => row.original)}
          columns={columns}
          onRowClick={handleRowClick}
        />
      ) : (
        /* Desktop Table View */
        <div className="table-wrapper">
          <Table className="table">
            <TableHeader id={TOUR_STEP_IDS.TABLE_SORT_BUTTONS}>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    className={onRowClick ? 'cursor-pointer hover:bg-accent' : ''}
                    onClick={() => onRowClick && onRowClick(row.original)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="h-24 text-center">
                    No results found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      <div className="flex items-center justify-between px-2">
        <div className="flex-1 text-sm text-muted-foreground">
          {table.getFilteredSelectedRowModel().rows.length} of{" "}
          {table.getFilteredRowModel().rows.length} row(s) selected.
        </div>
        <div className="flex items-center space-x-2">
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium">Rows per page</p>
            <Select
              value={`${table.getState().pagination.pageSize}`}
              onValueChange={(value) => {
                table.setPageSize(Number(value));
                handlePageSizeChange(Number(value));
              }}
            >
              <SelectTrigger id={TOUR_STEP_IDS.TABLE_RESULTS_SELECTOR} className="h-8 w-[70px]">
                <SelectValue placeholder={table.getState().pagination.pageSize} />
              </SelectTrigger>
              <SelectContent side="top">
                {[5, 10, 20, 30, 40, 50].map((size) => (
                  <SelectItem key={size} value={`${size}`}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex w-[100px] items-center justify-center text-sm font-medium">
            Page {table.getState().pagination.pageIndex + 1} of{" "}
            {table.getPageCount()}
          </div>
          <div className="flex items-center space-x-1" id={TOUR_STEP_IDS.TABLE_PAGE_SELECTOR}>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <span className="sr-only">Go to previous page</span>
              <span>‹</span>
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <span className="sr-only">Go to next page</span>
              <span>›</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
